import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from './lib/axios';

const AuthContext = createContext(null);
const BLOCKED_ACCOUNT_MESSAGE = 'This account has been blocked. Please contact support.';
const backendURL = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '');

const isUserBlocked = (user) => user?.isBlocked === true || user?.isActive === false;
const clearStoredSession = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('user');
  localStorage.removeItem('exp');
};

const persistSession = ({ token, user, exp }) => {
  if (!token) return;

  localStorage.setItem('accessToken', token);
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
  }
  if (exp) {
    localStorage.setItem('exp', exp);
  } else {
    localStorage.removeItem('exp');
  }
};

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);

  const applySession = ({ token, user, exp }) => {
    setUser(user ?? null);
    setAccessToken(token);
    setIsLoggedIn(Boolean(token));
    persistSession({ token, user, exp });
  };

  const fetchCurrentUser = async (tokenValue) => {
    const response = await axios.get(`/api/users/me`, {
      headers: {
        Authorization: `Bearer ${tokenValue}`,
      },
    });

    return response.data?.user ?? response.data?.doc ?? response.data ?? null;
  };

  // Check for existing session on mount
  useEffect(() => {
    let isMounted = true;

    const initializeSession = async () => {
      let token = localStorage.getItem('accessToken');
      const savedUser = localStorage.getItem('user');
      const exp = Number(localStorage.getItem('exp'));

      if (!token || !savedUser) {
        clearStoredSession();
        return;
      }

      if (Number.isFinite(exp) && exp <= Date.now() / 1000) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          if (isMounted) {
            setAccessToken(null);
            setUser(null);
            setIsLoggedIn(false);
          }
          clearStoredSession();
          return;
        }
        token = localStorage.getItem('accessToken');
      }

      try {
        const parsedUser = JSON.parse(savedUser);
        const hydratedUser = await fetchCurrentUser(token).catch(() => parsedUser);
        if (isUserBlocked(hydratedUser)) {
          if (isMounted) {
            setAccessToken(null);
            setUser(null);
            setIsLoggedIn(false);
          }
          clearStoredSession();
          return;
        }

        if (!isMounted) return;

        setAccessToken(token);
        setUser(hydratedUser);
        setIsLoggedIn(Boolean(token));
      } catch (error) {
        clearStoredSession();
      }
    };

    initializeSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`/api/users/login`, {
        email,
        password
      });

      if (response.status === 200) {
        let user = response.data.user ?? response.data.doc ?? null;
        const token = response.data.token ?? response.data.accessToken ?? null;
        const exp = response.data.exp ?? null;

        if (!token) {
          return {
            success: false,
            message: 'Login succeeded but no access token was returned.',
          };
        }

        if (!user) {
          try {
            user = await fetchCurrentUser(token);
          } catch (fetchUserError) {
            console.error('Failed to fetch current user after login:', fetchUserError);
          }
        }

        if (isUserBlocked(user)) {
          clearStoredSession();
          return {
            success: false,
            message: BLOCKED_ACCOUNT_MESSAGE,
          };
        }

        applySession({ token, user, exp });
        return { success: true };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      console.error('Login error:', error);
      const message =
        error.response?.data?.message ||
        (error.response?.status === 403 ? BLOCKED_ACCOUNT_MESSAGE : null) ||
        'Login failed. Please try again.';

      return { 
        success: false, 
        message,
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post (`/api/users`, {
        name: userData.name,
        email: userData.email,
        password: userData.password,
        phone: userData.phoneNumber,
        city: userData.city
      });

      if (response.status === 201) {
        // Handle successful registration and login
        if (response.data.doc) {
          return { success: true, user: response.data.doc};
        }
        
        return { success: false, message: response.data.message };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Registration failed. Please try again.' 
      };
    }
  };

  const requestLoginOtp = async (phoneNumber) => {
    try {
      const response = await axios.post(`/api/users/otp/login/request`, {
        phoneNumber,
        audience: 'client',
      });

      return {
        success: response.status === 200,
        orderId: response.data?.orderId || '',
        message: response.data?.message || 'OTP sent successfully on WhatsApp.',
      };
    } catch (error) {
      console.error('Request OTP error:', error);
      return {
        success: false,
        message: error.message || 'Failed to send OTP.',
      };
    }
  };

  const retryLoginOtp = async ({ orderId, phoneNumber }) => {
    try {
      const response = await axios.post(`/api/users/otp/login/resend`, {
        phoneNumber,
        orderId,
        audience: 'client',
      });

      return {
        success: response.status === 200,
        orderId: response.data?.orderId || orderId || '',
        message: response.data?.message || 'OTP resent successfully on WhatsApp.',
      };
    } catch (error) {
      console.error('Retry OTP error:', error);
      return {
        success: false,
        message: error.message || 'Failed to resend OTP.',
      };
    }
  };

  const verifyLoginOtp = async ({ phoneNumber, otp, orderId }) => {
    try {
      if (!backendURL) {
        return {
          success: false,
          message: 'Backend URL is not configured.',
        };
      }

      const response = await fetch(`${backendURL}/api/users/otp/login/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          audience: 'client',
          orderId,
          otp,
          phoneNumber,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        return {
          success: false,
          message: data?.message || 'Invalid OTP.',
        };
      }

      const token = data?.token ?? data?.accessToken ?? null;
      const exp = data?.exp ?? null;
      let user = data?.user ?? data?.doc ?? null;

      if (!token) {
        return {
          success: false,
          message: 'OTP verified but no access token was returned.',
        };
      }

      if (!user) {
        try {
          user = await fetchCurrentUser(token);
        } catch (fetchUserError) {
          console.error('Failed to fetch current user after OTP login:', fetchUserError);
        }
      }

      if (isUserBlocked(user)) {
        clearStoredSession();
        return {
          success: false,
          message: BLOCKED_ACCOUNT_MESSAGE,
        };
      }

      applySession({ token, user, exp });
      return { success: true };
    } catch (error) {
      console.error('Verify OTP error:', error);
      return {
        success: false,
        message: error.message || 'Invalid OTP.',
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/logout`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setAccessToken(null);
      setIsLoggedIn(false);
      clearStoredSession();
    }
  };

  const refreshToken = async () => {
    try {
      const response = await axios.post(
        `/api/users/refresh-token`,
        {},
        {
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        const newAccessToken =
          response.data.refreshedToken ?? response.data.token ?? response.data.accessToken;
        const exp = response.data.exp ?? null;

        if (!newAccessToken) {
          clearStoredSession();
          return false;
        }

        setAccessToken(newAccessToken);
        localStorage.setItem('accessToken', newAccessToken);
        if (exp) {
          localStorage.setItem('exp', exp);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      clearStoredSession();
      return false;
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider 
      value={{ 
        isLoggedIn, 
        setIsLoggedIn,
        user, 
        accessToken,
        login,
        requestLoginOtp,
        retryLoginOtp,
        verifyLoginOtp,
        register,
        logout,
        refreshToken,
        updateUser 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
